import { z } from 'zod';
import { Currency, Language } from '../enums.js';
import { TimestampSchema } from '../primitives.js';
/**
 * Prepaid billing (doc 42): a monthly platform fee per business + prepaid
 * minute packages that never expire + an optional, consented auto top-up.
 *
 * Units are fixed and never mixed: MONEY is integer minor units (grosze /
 * cents), TIME is integer seconds. There is no float minute anywhere in a
 * balance — a minute is a display unit.
 *
 * Every schema read on the call path (the projections on the business doc) is
 * fully defaulted AND lenient: the gateway parses the business on every inbound
 * call, so a bad stored value must suppress one feature, never the phone line.
 */
export const BillingMode = z.enum(['legacy', 'prepaid_v1']);
/** Bumped when what counts as a billable second changes (doc 42 §10.1). Stored
 * on every usage record so an old call can be explained by the rule it ran on. */
export const BILLING_POLICY_VERSION = 1;
/** The percentage choices for a low-balance warning (doc 42 §5). */
export const BILLING_WARN_PERCENT_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90];
export const BillingWarnSlot = z.enum(['first', 'second']);
export const BillingPaymentState = z.enum([
    'idle',
    /** A top-up order is open and waiting on Stripe. */
    'pending',
    /** The bank wants the cardholder (SCA) — shown as "confirm payment". */
    'requires_action',
    /** The last automatic attempt failed definitively. */
    'failed',
    /** Auto top-up would exceed the owner's monthly cap. */
    'cap_reached',
    /** Price/tax moved above what the owner consented to. */
    'price_review_required',
]);
/**
 * `business.billingSummary` — the owner-readable, SERVER-WRITTEN projection of
 * the wallet (doc 42 §7). The canonical aggregate lives in
 * `billingState/current`; this is updated in the same transaction.
 *
 * `balanceSeconds` is SIGNED: a call that ran past the funds leaves debt, and
 * hiding it behind `max(0, …)` would show an owner a balance they do not have.
 */
export const BillingSummarySchema = z
    .object({
    mode: BillingMode.catch('legacy').default('legacy'),
    currency: Currency.nullable().catch(null).default(null),
    balanceSeconds: z.number().int().catch(0).default(0),
    reservedSeconds: z.number().int().min(0).catch(0).default(0),
    paymentState: BillingPaymentState.catch('idle').default('idle'),
    activeTopupOrderId: z.string().nullable().catch(null).default(null),
    /** Expiry of the open manual invoice, so the UI can show "valid until". */
    activeTopupExpiresAt: TimestampSchema.nullable().catch(null).default(null),
    /**
     * Included (subscription) part of `balanceSeconds` (doc 59 N01). Purchased
     * credit is `balanceSeconds − includedSeconds`; the two expire differently,
     * so the UI shows them apart. Included seconds are only spendable while the
     * allowance period is current — readers use `effectiveAvailableSeconds`.
     */
    includedSeconds: z.number().int().min(0).catch(0).default(0),
    reservedIncludedSeconds: z.number().int().min(0).catch(0).default(0),
    /** The business's subscription offer (null = no included minutes). */
    offerVersion: z.string().nullable().catch(null).default(null),
    allowanceStatus: z.enum(['none', 'active', 'ended']).catch('none').default('none'),
    allowancePeriodStart: TimestampSchema.nullable().catch(null).default(null),
    allowancePeriodEnd: TimestampSchema.nullable().catch(null).default(null),
    allowanceGrantedSeconds: z.number().int().min(0).catch(0).default(0),
    /** A paid future period, not yet spendable (start of that period). */
    pendingAllowanceStart: TimestampSchema.nullable().catch(null).default(null),
    updatedAt: TimestampSchema.nullable().catch(null).default(null),
})
    .catch({
    mode: 'legacy',
    currency: null,
    balanceSeconds: 0,
    reservedSeconds: 0,
    paymentState: 'idle',
    activeTopupOrderId: null,
    activeTopupExpiresAt: null,
    includedSeconds: 0,
    reservedIncludedSeconds: 0,
    offerVersion: null,
    allowanceStatus: 'none',
    allowancePeriodStart: null,
    allowancePeriodEnd: null,
    allowanceGrantedSeconds: 0,
    pendingAllowanceStart: null,
    updatedAt: null,
});
/** Safe card summary for the UI. Never a PAN, never a Stripe secret. */
export const CardSummarySchema = z.object({
    brand: z.string().max(40),
    last4: z.string().max(4),
    expMonth: z.number().int(),
    expYear: z.number().int(),
});
/**
 * Auto top-up settings (doc 42 §4, §9). Per BUSINESS: consent for business A
 * never charges for business B, even on one Stripe customer.
 */
export const AutoTopupSettingsSchema = z.object({
    enabled: z.boolean().catch(false).default(false),
    packageId: z.string().nullable().catch(null).default(null),
    /** Fire when AVAILABLE seconds (after reservations) drop to this. */
    triggerSeconds: z.number().int().min(0).catch(1800).default(1800),
    /** Gross (with tax) of AUTOMATIC top-ups per calendar month. Null = never
     * chosen; auto top-up cannot be enabled without an explicit value. */
    monthlyGrossCapMinor: z.number().int().min(0).nullable().catch(null).default(null),
    /** Gross the owner agreed one purchase may cost. A price/tax change above
     * this stops automatic attempts (`price_review_required`). */
    maxGrossPerPurchaseMinor: z.number().int().min(0).nullable().catch(null).default(null),
    revision: z.number().int().min(0).catch(0).default(0),
});
/** `business.billingPreferences` — owner-readable, written only by callables. */
export const BillingPreferencesSchema = z
    .object({
    autoTopup: AutoTopupSettingsSchema.default({}),
    paymentMethod: CardSummarySchema.nullable().catch(null).default(null),
    /** IANA zone the monthly cap is counted in; fixed per period (doc 42 §9). */
    billingTimezone: z.string().nullable().catch(null).default(null),
})
    .catch({ autoTopup: AutoTopupSettingsSchema.parse({}), paymentMethod: null, billingTimezone: null });
const WarnPercent = z
    .number()
    .int()
    .refine((n) => BILLING_WARN_PERCENT_OPTIONS.includes(n));
export const BillingWarnRuleSchema = z.object({
    enabled: z.boolean().catch(false).default(false),
    remainingPercent: WarnPercent.catch(50).default(50),
});
export const BillingSmsRecipientMode = z.enum(['business_number', 'custom']);
/**
 * `business.notifications.billingSms` (doc 42 §5): SMS to the OWNER about
 * money. Not part of the client-SMS block and not behind its master switch.
 *
 * Lenient per field: a corrupted `customPhone` suppresses billing SMS with a
 * diagnostic, it never fails the business parse. The write boundary
 * (`saveBillingSmsSettings`) is strict.
 */
export const BillingSmsSettingsSchema = z
    .object({
    enabled: z.boolean().catch(false).default(false),
    recipientMode: BillingSmsRecipientMode.catch('business_number').default('business_number'),
    /** E.164 once validated by the callable; stored as a plain string so a bad
     * legacy value cannot break the parse. */
    customPhone: z.string().max(32).catch('').default(''),
    first: BillingWarnRuleSchema.default({ enabled: false, remainingPercent: 50 }),
    second: BillingWarnRuleSchema.default({ enabled: false, remainingPercent: 20 }),
    onTopup: z.boolean().catch(false).default(false),
    revision: z.number().int().min(0).catch(0).default(0),
})
    .catch({
    enabled: false,
    recipientMode: 'business_number',
    customPhone: '',
    first: { enabled: false, remainingPercent: 50 },
    second: { enabled: false, remainingPercent: 20 },
    onTopup: false,
    revision: 0,
});
/** Consent to one exact auto top-up configuration (doc 42 §9). */
export const AutoTopupConsentSchema = z.object({
    uid: z.string(),
    at: TimestampSchema,
    termsVersion: z.string(),
    /** Hash of package + gross ceiling + threshold + cap + terms. */
    configHash: z.string(),
    revision: z.number().int(),
});
/**
 * The CURRENT subscription allowance on a wallet (doc 59 N01/N03). `[periodStart,
 * periodEnd)` comes from the paid invoice's subscription line, in UTC.
 * `ended` means its unreserved seconds have already expired; seconds still
 * reserved by calls admitted inside the period may pay only for time before
 * `periodEnd` and expire when those calls settle.
 */
export const WalletAllowanceSchema = z.object({
    id: z.string(),
    subscriptionId: z.string(),
    periodStart: TimestampSchema,
    periodEnd: TimestampSchema,
    grantedSeconds: z.number().int().min(0),
    status: z.enum(['active', 'ended']),
});
/** A paid period whose start is still in the future — recorded, not spendable. */
export const PendingAllowanceSchema = z.object({
    id: z.string(),
    subscriptionId: z.string(),
    periodStart: TimestampSchema,
    periodEnd: TimestampSchema,
    seconds: z.number().int().min(0),
});
/**
 * A durable reason NOT to ask Stripe for a (paid) tax quote again (doc 59 F02).
 * Valid while `signature` — the inputs the reason depends on — is unchanged,
 * and, for transient failures, until `retryAt`.
 */
export const AutoQuoteHoldSchema = z.object({
    reason: z.enum(['price_review', 'tax_location', 'provider_error', 'cap_reached']),
    signature: z.string(),
    retryAt: TimestampSchema.nullable().default(null),
    failures: z.number().int().min(0).default(0),
    at: TimestampSchema,
});
/** `billingState/current` — the canonical, server-only wallet aggregate. */
export const BillingStateSchema = z.object({
    mode: BillingMode.default('prepaid_v1'),
    currency: Currency.default('PLN'),
    balanceSeconds: z.number().int().default(0),
    reservedSeconds: z.number().int().min(0).default(0),
    /** Σ over open calls of (maxCallSeconds − charged): what open calls could
     * still cost. `exposure − max(0, balance)` is the unfunded risk (§10.3). */
    openExposureSeconds: z.number().int().min(0).default(0),
    version: z.number().int().default(0),
    warningCycleId: z.string().nullable().default(null),
    warningBaselineSeconds: z.number().int().min(0).default(0),
    warnedSlots: z.array(BillingWarnSlot).default([]),
    /** Where the current cycle came from — a paid top-up, an allowance grant or a migration grant. */
    warningCycleSource: z.enum(['purchase', 'allowance', 'migration', 'operator']).nullable().default(null),
    activeTopupOrderId: z.string().nullable().default(null),
    /** Payable-until of the open MANUAL order (30 min, doc 42 §9). */
    activeTopupExpiresAt: TimestampSchema.nullable().default(null),
    stripeCustomerId: z.string().nullable().default(null),
    selectedPaymentMethodId: z.string().nullable().default(null),
    consent: AutoTopupConsentSchema.nullable().default(null),
    consentVersion: z.string().nullable().default(null),
    migrationVersion: z.string().nullable().default(null),
    /** When the last AUTO order was created — the 60 s cooldown (§9). */
    lastAutoOrderAt: TimestampSchema.nullable().default(null),
    /** A chargeback suspends new automatic charges until support clears it. */
    autoHold: z.enum(['chargeback', 'review', 'price_review', 'payment_failed']).nullable().default(null),
    paymentState: BillingPaymentState.default('idle'),
    // ── Subscription offer and included allowance (doc 59 N01) ───────────────
    /** Offer version (see `offer.ts`); null = prepaid without included minutes. */
    offerVersion: z.string().nullable().default(null),
    /** Paid periods starting before this instant never grant (offer upgrades
     *  start at the next renewal, doc 59 N08). */
    offerEffectiveFrom: TimestampSchema.nullable().default(null),
    /** Included part of `balanceSeconds` (≥ 0). Purchased = balance − included. */
    includedSeconds: z.number().int().min(0).default(0),
    /** Part of `reservedSeconds` drawn from included seconds. */
    reservedIncludedSeconds: z.number().int().min(0).default(0),
    allowance: WalletAllowanceSchema.nullable().default(null),
    pendingAllowance: PendingAllowanceSchema.nullable().default(null),
    /** Last provider reconciliation of the allowance (coalesces Stripe reads). */
    allowanceReconciledAt: TimestampSchema.nullable().default(null),
    // ── Auto top-up quote economy (doc 59 F02) ───────────────────────────────
    autoQuoteHold: AutoQuoteHoldSchema.nullable().default(null),
    /** Last authoritative gross for the auto package — used to pre-check the
     *  cap without a new paid quote; never used to charge. */
    lastAutoQuote: z
        .object({ priceId: z.string(), grossMinor: z.number().int().min(0), at: TimestampSchema })
        .nullable()
        .default(null),
    // ── Card setup recovery (doc 59 F01) ──────────────────────────────────────
    /** Revision of the newest setup attempt CREATED for this business. */
    cardSetupRevision: z.number().int().min(0).default(0),
    /** Revision of the setup attempt whose card is currently selected. An older
     *  attempt completing later can never replace a newer card. */
    cardSetupAppliedRevision: z.number().int().min(0).default(0),
    updatedAt: TimestampSchema.nullable().default(null),
});
export const LedgerKind = z.enum([
    'purchase',
    'usage',
    'migration_grant',
    'refund',
    'chargeback',
    'adjustment',
    /** Included seconds for one paid subscription period (doc 59 N02). */
    'allowance_grant',
    /** Unused included seconds removed at period end / supersession (N03). */
    'allowance_expire',
    /** Unused included seconds removed by a subscription refund (N06). */
    'allowance_reversal',
]);
export const LedgerSourceType = z.enum([
    'topup_order',
    'call',
    'migration',
    'stripe_refund',
    'stripe_dispute',
    'operator',
    'subscription_invoice',
    'allowance',
]);
/**
 * One immutable signed delta. Never updated, never deleted.
 *
 * `deltaSeconds = includedDeltaSeconds + purchasedDeltaSeconds`: every entry
 * says which credit it moved (doc 59 N01). Entries written before the split
 * existed default to purchased-only, which is what they were.
 */
export const LedgerEntrySchema = z.object({
    kind: LedgerKind,
    deltaSeconds: z.number().int(),
    includedDeltaSeconds: z.number().int().default(0),
    /** Null on pre-split entries: then it equals `deltaSeconds`. */
    purchasedDeltaSeconds: z.number().int().nullable().default(null),
    allowanceId: z.string().nullable().default(null),
    businessId: z.string(),
    billingModeVersion: z.number().int(),
    sourceType: LedgerSourceType,
    sourceId: z.string(),
    callKey: z.string().nullable().default(null),
    orderId: z.string().nullable().default(null),
    balanceAfter: z.number().int(),
    reason: z.string().max(200).default(''),
    createdAt: TimestampSchema,
});
export const TopupOrderStatus = z.enum([
    /** Created; no Stripe object yet. */
    'created',
    /** Invoice created/finalised; waiting for payment. */
    'open',
    /** Off-session payment submitted; the outcome is not known yet. */
    'processing',
    'requires_action',
    'paid',
    /** Credited to the wallet. Terminal. */
    'fulfilled',
    /** Definitively not paid (void / declined and given up). Terminal. */
    'void',
    /** Paid, but the payment did not match what was quoted. Needs a human. */
    'review',
]);
export const TopupQuoteSchema = z.object({
    quoteId: z.string(),
    businessId: z.string(),
    packageId: z.string(),
    catalogVersion: z.string(),
    currency: Currency,
    seconds: z.number().int().positive(),
    netMinor: z.number().int().min(0),
    taxMinor: z.number().int().min(0),
    grossMinor: z.number().int().min(0),
    stripePriceId: z.string(),
    stripeCustomerId: z.string().nullable(),
    /** Whether Stripe could compute tax for this customer. */
    taxStatus: z.enum(['complete', 'requires_location_inputs', 'failed', 'not_enabled']),
    expiresAt: TimestampSchema,
    createdAt: TimestampSchema,
});
export const TopupOrderSchema = z.object({
    businessId: z.string(),
    ownerUid: z.string(),
    /** `checkout`: a package bought with the first subscription payment —
     *  created already paid from the verified invoice, never driven. */
    purpose: z.enum(['manual', 'auto', 'checkout']),
    requestId: z.string(),
    status: TopupOrderStatus,
    quote: TopupQuoteSchema,
    /** Calendar month the auto cap reservation sits in; immutable. */
    periodKey: z.string().nullable().default(null),
    stripeCustomerId: z.string(),
    stripeInvoiceId: z.string().nullable().default(null),
    stripeInvoiceItemId: z.string().nullable().default(null),
    hostedInvoiceUrl: z.string().nullable().default(null),
    paymentMethodId: z.string().nullable().default(null),
    /** Persisted per deliberate payment attempt; a network retry reuses it. */
    paymentAttemptId: z.string().nullable().default(null),
    paymentAttempts: z.number().int().min(0).default(0),
    nextAttemptAt: TimestampSchema.nullable().default(null),
    lastError: z.string().nullable().default(null),
    expiresAt: TimestampSchema.nullable().default(null),
    fulfilledAt: TimestampSchema.nullable().default(null),
    /** Seconds refunded so far (whole-order refunds only in v1). */
    refundedSeconds: z.number().int().min(0).default(0),
    refundIds: z.array(z.string()).default([]),
    /** Reversal allocation review is independent of purchase fulfillment. */
    reversalReviews: z.record(z.object({ kind: z.enum(['refund', 'chargeback']), amountMinor: z.number().int() })).default({}),
    consentRevision: z.number().int().nullable().default(null),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema.nullable().default(null),
});
/** Owner-readable history row — the safe projection of an order. */
export const BillingHistoryItemSchema = z.object({
    orderId: z.string(),
    /** `subscription`: a paid platform-subscription invoice (display only). */
    purpose: z.enum(['manual', 'auto', 'checkout', 'migration', 'subscription']),
    status: z.enum(['pending', 'requires_action', 'paid', 'failed', 'void', 'refunded', 'partially_refunded', 'review']),
    currency: Currency.nullable().default(null),
    netMinor: z.number().int().default(0),
    taxMinor: z.number().int().default(0),
    grossMinor: z.number().int().default(0),
    seconds: z.number().int().default(0),
    /** Posted balance right after this purchase was credited. */
    balanceAfterSeconds: z.number().int().nullable().default(null),
    invoiceUrl: z.string().nullable().default(null),
    receiptUrl: z.string().nullable().default(null),
    refundedSeconds: z.number().int().default(0),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema.nullable().default(null),
});
/** Per-call metering record, `billingUsage/{callKey}` (doc 42 §10.2). */
export const BillingUsageSchema = z.object({
    businessId: z.string(),
    callKey: z.string(),
    state: z.enum(['reserved', 'active', 'closed']),
    policyVersion: z.number().int(),
    /** Fixed at admission; a settings change never extends it. */
    maxCallSeconds: z.number().int().positive(),
    /** Highest cumulative billable milliseconds applied. Monotonic. */
    appliedMs: z.number().int().min(0).default(0),
    chargedSeconds: z.number().int().min(0).default(0),
    reservedSeconds: z.number().int().min(0).default(0),
    /** Part of `reservedSeconds` drawn from an allowance (doc 59 N04). */
    reservedIncludedSeconds: z.number().int().min(0).default(0),
    /** Which allowance that included reservation belongs to, and that
     *  allowance's period: the reservation may pay only for billable time
     *  inside it (doc 61 R01). Start is null on pre-doc-61 records. */
    includedAllowanceId: z.string().nullable().default(null),
    includedAllowanceStart: TimestampSchema.nullable().default(null),
    includedAllowanceEnd: TimestampSchema.nullable().default(null),
    /** Source allocation of `chargedSeconds` (included + purchased = charged). */
    includedChargedSeconds: z.number().int().min(0).default(0),
    purchasedChargedSeconds: z.number().int().min(0).default(0),
    billableStartedAt: TimestampSchema.nullable().default(null),
    leaseOwner: z.string().nullable().default(null),
    leaseUntil: TimestampSchema.nullable().default(null),
    closeReason: z.string().nullable().default(null),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema.nullable().default(null),
});
/** Auto top-up money per calendar month (doc 42 §9). */
export const BillingPeriodSchema = z.object({
    periodKey: z.string(),
    grossSpentMinor: z.number().int().min(0).default(0),
    grossPendingMinor: z.number().int().min(0).default(0),
    /** orderId → gross reserved; released or moved to spent exactly once. */
    pendingOrders: z.record(z.number().int()).default({}),
    settledOrders: z.array(z.string()).default([]),
});
export const OutboxKind = z.enum([
    'sms_low_balance',
    'sms_topup',
    'auto_topup_eval',
    'topup_order',
    /** Minutes at the auto top-up threshold while auto top-up is off. */
    'sms_auto_off',
    /** An automatic top-up did not happen (declined, no card, cap…). */
    'sms_auto_failed',
]);
export const OutboxState = z.enum([
    'pending',
    'leased',
    'submitted',
    'delivered',
    'retryable_failed',
    'permanent_failed',
    'suppressed',
    'unknown',
    'done',
]);
export const OutboxJobSchema = z.object({
    businessId: z.string(),
    kind: OutboxKind,
    state: OutboxState,
    /** For SMS: the warning cycle it belongs to; stale cycles are suppressed. */
    cycleId: z.string().nullable().default(null),
    slots: z.array(BillingWarnSlot).default([]),
    orderId: z.string().nullable().default(null),
    /** `sms_auto_failed`: the AutoTopupFailReason the message states. */
    smsReason: z.string().nullable().default(null),
    attempts: z.number().int().min(0).default(0),
    nextAttemptAt: TimestampSchema.nullable().default(null),
    leaseToken: z.string().nullable().default(null),
    leaseUntil: TimestampSchema.nullable().default(null),
    /** Masked — the full number is re-resolved at send time. */
    maskedTo: z.string().nullable().default(null),
    language: Language.nullable().default(null),
    body: z.string().nullable().default(null),
    segments: z.number().int().nullable().default(null),
    provider: z.string().nullable().default(null),
    providerMessageId: z.string().nullable().default(null),
    /** Persisted BEFORE SMS submission. An expired lease with this marker is
     * uncertain delivery and must never be blindly sent again. */
    smsSubmissionStartedAt: TimestampSchema.nullable().default(null),
    /** The carrier's delivery report, written only by the report webhook. */
    delivery: z.enum(['sent', 'failed', 'delivered']).nullable().default(null),
    suppressReason: z.string().nullable().default(null),
    error: z.string().nullable().default(null),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema.nullable().default(null),
});
/**
 * One subscription-period entitlement, `billingAllowances/{allowanceId}`
 * (doc 59 N01–N03, N06). Created exactly once per business/subscription/item/
 * period; its status is the audit trail of what that period was worth.
 */
export const AllowanceStatus = z.enum([
    /** Paid period not started yet. */
    'scheduled',
    /** Granted to the wallet. */
    'active',
    /** Period over; unused seconds expired. */
    'expired',
    /** Replaced by an overlapping newer paid period (resubscription). */
    'superseded',
    /** Full subscription refund: unused seconds removed. */
    'revoked',
    /** Paid, but the period had already ended — recorded, never spendable. */
    'historical',
    /** Needs a human (discount, customer credit, dispute, overlap, partial refund). */
    'review',
]);
export const AllowanceRecordSchema = z.object({
    businessId: z.string(),
    offerVersion: z.string(),
    subscriptionId: z.string(),
    subscriptionItemId: z.string(),
    priceId: z.string(),
    periodStart: TimestampSchema,
    periodEnd: TimestampSchema,
    invoiceId: z.string(),
    chargeId: z.string().nullable().default(null),
    livemode: z.boolean().default(false),
    status: AllowanceStatus,
    reason: z.string().nullable().default(null),
    grantedSeconds: z.number().int().min(0).default(0),
    consumedSeconds: z.number().int().min(0).default(0),
    expiredSeconds: z.number().int().min(0).default(0),
    reversedSeconds: z.number().int().min(0).default(0),
    /** Highest cumulative refunded amount applied for this period's charge
     *  (doc 61 R03): refunds are processed by amount, not by key. */
    refundedMinor: z.number().int().min(0).default(0),
    reversalIds: z.array(z.string()).default([]),
    reviews: z.record(z.object({ kind: z.string(), amountMinor: z.number().int() })).default({}),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema.nullable().default(null),
});
/**
 * One server-created card-setup attempt, `billingSetupAttempts/{sessionId}`
 * (doc 59 F01). Completion — by the browser return, the webhook or the
 * reconciler — is a no-op after the first, and an attempt older than the
 * applied one never replaces the selected card.
 */
export const SetupAttemptSchema = z.object({
    businessId: z.string(),
    stripeCustomerId: z.string(),
    revision: z.number().int().min(1),
    status: z.enum(['open', 'completed', 'superseded', 'failed', 'expired']),
    paymentMethodId: z.string().nullable().default(null),
    error: z.string().nullable().default(null),
    createdAt: TimestampSchema,
    completedAt: TimestampSchema.nullable().default(null),
    updatedAt: TimestampSchema.nullable().default(null),
});
//# sourceMappingURL=schemas.js.map